import React, { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, WifiOff } from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";

/**
 * DesktopActivationBanner — desktop-only banner (mirrors PlanLimitBanner's
 * pattern) offering one-click device activation against the signed
 * entitlement issued by POST /licensing/activate.
 *
 * Deliberately a dismissible banner, NOT a hard gate that blocks the app:
 * this is new, first-iteration licensing code, and every existing Desktop
 * install currently has zero entitlement records, so a gate would lock out
 * every existing user on their very next launch if anything here has a
 * bug. A future iteration can tighten this once the flow has real-world
 * mileage (see plan doc's Phase 2 design notes).
 *
 * Only ever rendered when isDesktop() is true (see Layout.jsx) -- window.moovbyte
 * is guaranteed to exist wherever this mounts.
 */
export default function DesktopActivationBanner() {
    const [status, setStatus] = useState(null); // null = loading
    const [activating, setActivating] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [offlineBusy, setOfflineBusy] = useState(false);

    const refreshStatus = async () => {
        try {
            const s = await window.moovbyte.licensing.getStatus();
            setStatus(s);
        } catch {
            setStatus({ hasEntitlement: false });
        }
    };

    useEffect(() => { refreshStatus(); }, []);

    if (dismissed || !status) return null;
    // Valid, not in the offline grace window -- nothing to show.
    if (status.hasEntitlement && status.ok !== false && !status.offline) return null;

    const activate = async () => {
        setActivating(true);
        try {
            const fp = await window.moovbyte.licensing.getDeviceFingerprint();
            if (!fp?.ok) throw new Error(fp?.error || "Could not read device identity");
            const hostname = await window.moovbyte.getHostname().catch(() => "MoovBytes Desktop");

            const { data } = await http.post("/licensing/activate", {
                device_name: hostname,
                device_fingerprint: fp.fingerprint,
                kind: "desktop_app",
            });
            if (!data.entitlement) throw new Error("Activation succeeded but no entitlement was issued");

            const saved = await window.moovbyte.licensing.saveEntitlement(data.entitlement, data.id);
            if (!saved?.ok) throw new Error(saved?.error || "Entitlement failed local verification");

            toast.success("Device activated.");
            await refreshStatus();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const message = typeof detail === "string" ? detail
                : detail?.error === "plan_limit_exceeded" ? "Activation limit reached. Deactivate a device or upgrade your plan."
                : e.message || "Activation failed.";
            toast.error(message);
        } finally {
            setActivating(false);
        }
    };

    // §26: for a machine with no route to the backend at all. Generates a
    // plain (unsigned -- Desktop has no private key) JSON request file the
    // customer carries to any online machine, uploads in the Client Portal
    // (PortalDevices.jsx "Import offline activation"), and brings the
    // resulting signed entitlement back to import here.
    const exportRequest = async () => {
        setOfflineBusy(true);
        try {
            const fp = await window.moovbyte.licensing.getDeviceFingerprint();
            if (!fp?.ok) throw new Error(fp?.error || "Could not read device identity");
            const hostname = await window.moovbyte.getHostname().catch(() => "MoovBytes Desktop");
            const result = await window.moovbyte.licensing.exportActivationRequest(hostname, fp.fingerprint, "desktop_app");
            if (result?.canceled) return;
            if (!result?.ok) throw new Error(result?.error || "Could not save activation request");
            toast.success("Activation request saved. Upload it in the Client Portal from any device with internet access.");
        } catch (e) {
            toast.error(e.message || "Could not create activation request.");
        } finally {
            setOfflineBusy(false);
        }
    };

    const importFile = async () => {
        setOfflineBusy(true);
        try {
            const result = await window.moovbyte.licensing.importActivationFile();
            if (result?.canceled) return;
            if (!result?.ok) throw new Error(result?.error || "That file isn't a valid activation.");
            toast.success("Device activated from file.");
            await refreshStatus();
        } catch (e) {
            toast.error(e.message || "Import failed.");
        } finally {
            setOfflineBusy(false);
        }
    };

    const OfflineActivationLinks = () => (
        <span className="ml-2 flex items-center gap-2 border-l border-current/20 pl-2 text-[10.5px] opacity-80">
            <button onClick={exportRequest} disabled={offlineBusy} data-testid="desktop-activation-banner-export-request" className="underline hover:opacity-100 disabled:opacity-50">
                No internet? Save activation request
            </button>
            <button onClick={importFile} disabled={offlineBusy} data-testid="desktop-activation-banner-import-file" className="underline hover:opacity-100 disabled:opacity-50">
                Import activation file
            </button>
        </span>
    );

    // Past the offline grace window -- entitlement exists but is no longer
    // usable. Distinguishing this from "never activated" per §69's
    // differentiated-messaging requirement.
    if (status.hasEntitlement && status.ok === false) {
        return (
            <Banner tone="red" testid="desktop-activation-banner-expired">
                <ShieldAlert size={14} />
                <span>
                    <b>Device activation needs to be refreshed.</b> Reconnect and reactivate to continue.
                </span>
                <ActivateBtn onClick={activate} busy={activating} label="Reactivate" />
                <OfflineActivationLinks />
            </Banner>
        );
    }

    // Within the offline grace window -- working, but flag it so the user
    // isn't surprised when it eventually stops.
    if (status.offline) {
        const days = Math.max(1, Math.ceil(status.graceRemainingMs / (24 * 60 * 60 * 1000)));
        return (
            <Banner tone="amber" testid="desktop-activation-banner-offline">
                <WifiOff size={14} />
                <span>
                    Working offline on a previously verified activation. Reconnect within{" "}
                    <b>{days} day{days === 1 ? "" : "s"}</b> to keep using MoovBytes.
                </span>
                <button
                    onClick={() => setDismissed(true)}
                    className="ml-auto text-[11px] uppercase tracking-widest opacity-70 hover:opacity-100"
                    data-testid="desktop-activation-banner-dismiss"
                >
                    Dismiss
                </button>
            </Banner>
        );
    }

    // Never activated on this device.
    return (
        <Banner tone="blue" testid="desktop-activation-banner-inactive">
            <ShieldCheck size={14} />
            <span>This device isn't activated yet.</span>
            <ActivateBtn onClick={activate} busy={activating} label="Activate this device" />
            <OfflineActivationLinks />
        </Banner>
    );
}

function Banner({ tone, testid, children }) {
    const toneCls = {
        red:   "border-[hsl(var(--destructive))]/50 bg-[hsl(var(--destructive))]/[0.08] text-[hsl(var(--destructive))]",
        amber: "border-amber-500/50 bg-amber-500/[0.08] text-amber-300",
        blue:  "border-[#5b5ef0]/50 bg-[#5b5ef0]/[0.08] text-[#a5a8ff]",
    }[tone];
    return (
        <div
            data-testid={testid}
            className={`flex flex-wrap items-center gap-2 border-b border-l-4 px-4 py-2 text-xs ${toneCls}`}
        >
            {children}
        </div>
    );
}

function ActivateBtn({ onClick, busy, label }) {
    return (
        <button
            onClick={onClick}
            disabled={busy}
            data-testid="desktop-activation-banner-activate-btn"
            className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-foreground px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-background hover:opacity-90 disabled:opacity-50"
        >
            <ShieldCheck size={11} /> {busy ? "Activating…" : label}
        </button>
    );
}
